import React from "react";
import T from '../../components/common/T';
import Price from "../../components/common/Price";
import { useAssets } from "../../context/AssetContext";
import KanbanBoard from "../../components/common/KanbanBoard";
import { 
  CheckCircleIcon, 
  ExclamationTriangleIcon,
  ArchiveBoxXMarkIcon,
  TruckIcon
} from "@heroicons/react/24/outline";

const AssetKanbanView = () => {
  const { assets, updateAsset } = useAssets();

  // Column footer logic
  const renderAssetFooter = (colAssets) => {
    if (colAssets.length === 0) return null;
    const totalValue = colAssets.reduce((sum, a) => sum + (parseFloat(a.cost_rate) || 0), 0);
    return (
      <div className="flex justify-between items-center text-xs">
        <span className="font-bold text-gray-500 uppercase tracking-wider"><T>Total Rate</T></span>
        <span className="font-bold text-indigo-600"><Price amount={totalValue.toFixed(2)} />/hr</span>
      </div>
    );
  };

  const COLUMNS = [
    { 
      id: "Available", 
      title: "Available", 
      icon: CheckCircleIcon,
      iconColor: "text-emerald-500",
      renderFooter: renderAssetFooter
    },
    { 
      id: "In Use", 
      title: "In Use", 
      icon: TruckIcon,
      iconColor: "text-blue-500",
      renderFooter: renderAssetFooter
    },
    { 
      id: "Under Maintenance", 
      title: "Maintenance", 
      icon: ExclamationTriangleIcon,
      iconColor: "text-amber-500",
      renderFooter: renderAssetFooter
    },
    { 
      id: "Retired", 
      title: "Retired", 
      icon: ArchiveBoxXMarkIcon,
      iconColor: "text-gray-500",
    }
  ];

  const handleDragEnd = (result) => {
    const { destination, draggableId } = result;
    if (!destination) return;
    updateAsset(draggableId, { status: destination.droppableId });
  };

  // CLEANED CARD UI
  const renderAssetCard = (asset) => (
    <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition cursor-grab active:cursor-grabbing flex flex-col min-h-[130px]">
      
      {/* Top: Title & Badge */}
      <div className="flex justify-between items-start gap-2">
        <div className="overflow-hidden">
          <h4 className="font-bold text-gray-900 text-base leading-tight truncate">
            {asset.name}
          </h4>
          <p className="text-[11px] text-gray-500 font-mono mt-1">
            {asset.code || 'NO-CODE'}
          </p>
        </div>
        <span className="text-[10px] bg-gray-100 text-gray-600 px-2 py-1 rounded font-bold uppercase tracking-wider border border-gray-200 shrink-0">
          {asset.category || 'General'}
        </span>
      </div>

      {/* Bottom: Stats Grid (mt-auto pushes it to the bottom) */}
      <div className="mt-auto pt-3 border-t border-gray-100 grid grid-cols-2 gap-3">
        <div className="overflow-hidden">
          <span className="block text-[10px] text-gray-400 font-bold uppercase tracking-wider mb-0.5">
            <T>Location</T>
          </span>
          <span className="text-xs font-bold text-gray-700 truncate block">
            {asset.location || "Warehouse"}
          </span>
        </div>
        <div className="overflow-hidden">
          <span className="block text-[10px] text-gray-400 font-bold uppercase tracking-wider mb-0.5">
            <T>Rate</T>
          </span>
          <span className="text-xs font-bold text-gray-700 truncate block">
            {asset.cost_rate ? `₹${asset.cost_rate}/hr` : "—"}
          </span>
        </div>
      </div>

    </div>
  );

  return (
    <KanbanBoard 
      columns={COLUMNS}
      items={assets}
      groupBy="status"
      onDragEnd={handleDragEnd}
      renderCard={renderAssetCard}
    />
  );
};

export default AssetKanbanView;